import { create } from "zustand";
import { persist } from "zustand/middleware";

// ============================================================================
// Single source of truth for theming.
//
// Resolution: 'light' / 'dark' are explicit user choices that override the OS.
// 'system' (the default when nothing is stored) follows prefers-color-scheme.
// The DOM is driven ONLY through data-theme + color-scheme on <html>; the CSS
// token system (src/index.css) does the rest. There is no inline-style color
// palette in JS (the former useTheme store, which shadowed the CSS tokens with
// a stale palette, was removed — see docs/design-tokens.md).
// ============================================================================

export type Theme = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "writerslogic-theme-mode";
/** Old useTheme color-store key; migrated once into STORAGE_KEY then deleted. */
const LEGACY_KEY = "writerslogic-theme-colors";

// theme-color drives mobile browser chrome (address bar). Match the canonical
// canvas backgrounds from the token system.
const THEME_COLOR: Record<ResolvedTheme, string> = {
  dark: "#17212D",
  light: "#F5F0E6",
};

/**
 * Pure resolution rule. Explicit choice wins; anything else (incl. 'system')
 * resolves to dark. The light (vellum) theme is only partially migrated — the
 * content backgrounds are still hard-coded dark, so on a light-mode OS 'system'
 * produced a broken half-light shell (light nav + light muddy-gold accents over a
 * dark body). Until the light theme is completed, dark is the default; light
 * remains an explicit opt-in. Kept dependency-free so it is unit-testable.
 */
export function resolveTheme(
  theme: Theme,
  _systemPrefersLight: boolean,
): ResolvedTheme {
  if (theme === "light" || theme === "dark") return theme;
  return "dark";
}

export function systemPrefersLight(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: light)").matches
  );
}

/**
 * The single DOM mutation point: reflect the resolved theme onto <html> via
 * data-theme (drives the CSS tokens) and color-scheme (native form controls,
 * scrollbars, and browser chrome), plus the theme-color meta tag.
 */
export function applyResolvedTheme(resolved: ResolvedTheme): void {
  if (typeof document === "undefined") return;
  const el = document.documentElement;
  el.setAttribute("data-theme", resolved);
  el.style.colorScheme = resolved;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", THEME_COLOR[resolved]);
}

/**
 * One-time migration from the removed useTheme store's persisted key. Returns
 * the migrated explicit choice (or null), and clears the stale key. Only runs
 * when the new key is absent, so it never clobbers a current preference.
 */
export function migrateLegacyThemeKey(): ResolvedTheme | null {
  if (typeof localStorage === "undefined") return null;
  try {
    if (localStorage.getItem(STORAGE_KEY)) return null;
    const raw = localStorage.getItem(LEGACY_KEY);
    if (!raw) return null;
    const mode = JSON.parse(raw)?.state?.mode;
    const migrated = mode === "light" || mode === "dark" ? mode : null;
    // Persist the migrated preference into the new key BEFORE deleting the old
    // one, so the choice is durable even if zustand never writes the initial
    // state (a reload before any toggle would otherwise lose it). Shape matches
    // zustand persist's { state: partialize(...), version }.
    if (migrated) {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ state: { theme: migrated }, version: 0 }),
      );
    }
    localStorage.removeItem(LEGACY_KEY);
    return migrated;
  } catch {
    return null;
  }
}

interface ThemeState {
  /** The user's stored preference. */
  theme: Theme;
  /** The concrete theme currently applied (never 'system'). */
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const migrated = migrateLegacyThemeKey();
const initialTheme: Theme = migrated ?? "system";

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: initialTheme,
      resolvedTheme: resolveTheme(initialTheme, systemPrefersLight()),

      setTheme: (theme) => {
        const resolved = resolveTheme(theme, systemPrefersLight());
        applyResolvedTheme(resolved);
        set({ theme, resolvedTheme: resolved });
      },

      // Toggling flips to the opposite of what is currently shown and records it
      // as an explicit choice (so it now overrides the OS).
      toggleTheme: () => {
        const next: ResolvedTheme =
          get().resolvedTheme === "dark" ? "light" : "dark";
        applyResolvedTheme(next);
        set({ theme: next, resolvedTheme: next });
      },
    }),
    {
      name: STORAGE_KEY,
      // Only the preference is persisted; resolvedTheme is derived on rehydrate.
      partialize: (state) => ({ theme: state.theme }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const resolved = resolveTheme(state.theme, systemPrefersLight());
        state.resolvedTheme = resolved;
        applyResolvedTheme(resolved);
      },
    },
  ),
);

// Follow OS changes only while the preference is 'system'.
if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
  window
    .matchMedia("(prefers-color-scheme: light)")
    .addEventListener("change", (e) => {
      if (useThemeStore.getState().theme !== "system") return;
      const resolved: ResolvedTheme = e.matches ? "light" : "dark";
      applyResolvedTheme(resolved);
      useThemeStore.setState({ resolvedTheme: resolved });
    });
}
